import { useQuery, type QueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'

import { userQueryOptions } from '@/api/users'
import type { IUser } from '@/types/user'

/** Under the router's `defaultPendingMs`, so waiting on the name never shows its pending screen. */
const CRUMB_WAIT_MS = 200

function displayName(user: IUser | undefined): { crumb?: string } {
    return { crumb: user?.details?.dname || user?.details?.name || user?.email || undefined }
}

/**
 * The loader for a page about one account, so its breadcrumb -- and with it the tab
 * title and the route announcement -- names the person instead of saying "User".
 *
 * It reads the page's own query, so it warms the cache the page then renders from
 * rather than fetching twice, and a cached user resolves without a request. It never
 * throws: the page has its own error state with a retry, and a failed lookup here only
 * means the crumb falls back to the route's static label.
 */
export async function loadUserCrumb({
    context,
    params,
    preload
}: {
    context: { queryClient: QueryClient }
    params: { userId: string }
    preload: boolean
}): Promise<{ crumb?: string }> {
    const options = userQueryOptions(params.userId)

    /*
     * A hover preload only reads the cache. Looking a user up is a scan over every
     * account, and a pointer drifting down a list of user links would start one per
     * row it crossed. The navigation that follows finds this preload stale and fills
     * the crumb in behind an already-rendered page.
     */
    if (preload) return displayName(context.queryClient.getQueryData(options.queryKey))

    /*
     * Waits only briefly. That scan can take seconds, the page has its own skeleton for
     * it, and holding the navigation on the crumb alone would swap that skeleton for the
     * router's generic pending screen. A slower lookup goes on warming the page's query
     * under its normal retry policy, and `useLateUserCrumb` fills the name in when it lands.
     */
    const timeout = new Promise<undefined>(resolve => setTimeout(resolve, CRUMB_WAIT_MS))
    try {
        return displayName(await Promise.race([context.queryClient.ensureQueryData(options), timeout]))
    } catch {
        return {}
    }
}

/**
 * The other half of `loadUserCrumb`'s short wait: once the page's own query has the user,
 * reloads the route's loader, which now answers from the cache. Observes without fetching,
 * so it adds no request and no permission check of its own.
 */
export function useLateUserCrumb(routeId: string, userId: string, crumb: string | undefined) {
    const router = useRouter()
    const loaded = useQuery({ ...userQueryOptions(userId), enabled: false }).data !== undefined

    useEffect(() => {
        if (loaded && crumb === undefined) void router.invalidate({ filter: match => match.routeId === routeId })
    }, [loaded, crumb, router, routeId])
}
